import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, pairwise, take, takeUntil } from 'rxjs/operators';

import { CurrentUserService } from '@core/account-logic';
import type { PricingFeatureSubscription } from '@core/billing-types';
import { BillingPlanTier } from '@core/billing-types';
import { ENVIRONMENT } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { I18nService } from '@core/i18n-legacy';
import type { IntercomEnvironment } from '@core/intercom-integration-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import type { SupportedLanguage } from '@core/languages-legacy';
import { getProductCodenameFromSubscription } from '@recruitee/billing-util';

type LocalisedSurvey = Record<SupportedLanguage, number>;

function getProductCodename(currentCompany: Company): BillingPlanTier | null {
  const currentCompanySubscription = <PricingFeatureSubscription>currentCompany!.subscription;

  return (currentCompany && getProductCodenameFromSubscription(currentCompanySubscription)) || null;
}

@Injectable()
export class ProductOrderTrackingService {
  private readonly store = inject(Store);
  private readonly intercomService = inject(IntercomService);
  private readonly i18n = inject(I18nService);
  private readonly environment = inject<IntercomEnvironment>(ENVIRONMENT, { optional: true });

  private readonly currentUserData = inject(CurrentUserService);
  private ngOnDestroy$: Subject<void> = new Subject<void>();
  private launchToScaleUpgrade$ = this.currentUserData.currentCompany$.pipe(
    map(currentCompany => getProductCodename(currentCompany)),
    pairwise(),
    filter(
      ([prevProductCode, currProductCode]) =>
        prevProductCode === BillingPlanTier.Launch && currProductCode === BillingPlanTier.Scale,
    ),
  );

  constructor() {
    this.launchToScaleUpgrade$.pipe(takeUntil(this.ngOnDestroy$), take(1)).subscribe(() => {
      const localisedSurvey = this.environment?.intercom?.surveys?.pricing;
      if (!localisedSurvey) return;

      const surveyId = localisedSurvey[<keyof LocalisedSurvey>this.i18n.locale];
      if (!surveyId) return;

      this.intercomService.scheduleSurvey(surveyId);
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
